// Copyright 2025, University of Maryland, All Rights Reserved
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { apiClient } from '../api/api-client'

/** Local type for contracts summary dataset from API */
interface ContractsSummaryDataset {
  documentId: string
  datasetKey?: string
  headline?: string
  summary?: string
  updatedAt?: string
  reportOverview?: Record<string, unknown>
}

interface QueryParams {
  documentId?: string
  orgId?: string
  workspaceId?: string
  /** Agent owning the dataset — its key is only unique within one. */
  agentId?: string
  datasetKey?: string
  headline?: string
}

const parseQueryParams = (search: string): QueryParams => {
  const params = new URLSearchParams(search)
  return {
    documentId: params.get('documentId') || undefined,
    orgId: params.get('orgId') || undefined,
    workspaceId: params.get('workspaceId') || undefined,
    agentId: params.get('agentId') || undefined,
    datasetKey: params.get('datasetKey') || undefined,
    headline: params.get('headline') || undefined,
  }
}

const DocumentDashboardContractsPrintPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [summary, setSummary] = useState<ContractsSummaryDataset | null>(null)

  const query = parseQueryParams(location.hash.split('?')[1] || '')

  useEffect(() => {
    const fetchSummary = async () => {
      if (!query.documentId || !query.orgId || !query.workspaceId || !query.agentId || !query.datasetKey) {
        setError('Missing required parameters for print view')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        interface DatasetPayload {
          payload: Record<string, unknown>
        }
        const url = `/org/${query.orgId}/ws/${query.workspaceId}/datasets/documents/${query.documentId}/${query.agentId}/${query.datasetKey}`
        const response = await apiClient.get<DatasetPayload>(url)
        const summaryData = response.data.payload as unknown as ContractsSummaryDataset
        setSummary(summaryData)
      } catch (err: unknown) {
        const error = err as { response?: { data?: { detail?: string } }; message?: string }
        const message = error?.response?.data?.detail || error?.message || 'Failed to load printable data.'
        console.error('[PrintPage] Failed to load summary', {
          documentId: query.documentId,
          error: message,
        })
        setError(message)
      } finally {
        setLoading(false)
      }
    }

    void fetchSummary()
  }, [query.documentId, query.orgId, query.workspaceId, query.agentId, query.datasetKey])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Preparing print view...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="max-w-md rounded-lg border border-destructive/50 bg-destructive/10 p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <div>
              <h3 className="font-semibold text-destructive">Failed to load print view</h3>
              <p className="mt-1 text-sm text-muted-foreground">{error}</p>
              <button
                onClick={() => navigate(-1)}
                className="mt-4 text-sm text-primary underline-offset-4 hover:underline"
              >
                Go back
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!summary) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">No analysis data available</p>
      </div>
    )
  }

  // Print-friendly layout
  return (
    <div className="print-page mx-auto max-w-4xl p-8">
      <style>{`
        @media print {
          body { margin: 0; padding: 0; }
          .print-page { max-width: 100%; }
        }
      `}</style>

      <div className="mb-8">
        <h1 className="text-3xl font-bold">{query.headline || summary.headline || 'Contract Analysis'}</h1>
        {summary.updatedAt && (
          <p className="mt-2 text-sm text-muted-foreground">
            Updated: {new Date(summary.updatedAt).toLocaleString()}
          </p>
        )}
      </div>

      {summary.summary && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-3">Executive Summary</h2>
          <div className="prose prose-sm max-w-none">
            <p className="text-gray-700 whitespace-pre-wrap">{summary.summary}</p>
          </div>
        </div>
      )}

      {summary.reportOverview && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-3">Report Overview</h2>
          <div className="rounded-lg border bg-gray-50 p-4">
            <pre className="text-sm overflow-auto">
              {JSON.stringify(summary.reportOverview, null, 2)}
            </pre>
          </div>
        </div>
      )}

      <div className="mt-12 border-t pt-4">
        <p className="text-xs text-muted-foreground">
          Document ID: {summary.documentId}
        </p>
        {summary.datasetKey && (
          <p className="text-xs text-muted-foreground">
            Dataset: {summary.datasetKey}
          </p>
        )}
      </div>
    </div>
  )
}

export default DocumentDashboardContractsPrintPage
